import { type CSSProperties, useMemo } from 'react';

import classNames from 'classnames';

import { getBackgroundColorStyle } from '../../../../utils/background';
import { computeColor } from '../../../../utils/colors';
import { computeFont } from '../../../../utils/fonts';
import { getFontSizeVariables } from '../../../../utils/getFontSizeVariables';

import CaretDown from '../../../../icon/CaretDown';
import * as styles from '../../../Navbar.css';
import { NAVBAR_MENU_ITEM_BORDER_DEFAULTS, NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2 } from '../../NavbarMenuItem.constants';
import type { NavbarMenuItemV2ElementProps, TNavbarItemV2ButtonElement } from '../../NavbarMenuItem.type';
import { DropdownContent } from './DropdownContent';

interface AccordionProps extends NavbarMenuItemV2ElementProps {
  labelComponent?: React.ReactNode;
  showCaret?: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isInEditor?: boolean;
  shouldForceOpen?: boolean;
  evaluatedHoverBorderRadius?: string | number;
}

export function Accordion({
  element,
  className,
  children,
  customAttributes,
  labelComponent,
  showCaret = true,
  isOpen,
  setIsOpen,
  screen,
  isInEditor,
  shouldForceOpen = false,
  evaluatedHoverBorderRadius,
  ...props
}: AccordionProps) {
  const {
    label,
    padding,
    background,
    borderRadius,
    color,
    borderColor,
    borderWidth,
    borderStyle,
    hoverBorderColor,
    hoverBorderStyle,
    hoverBorderWidth,
    hoverTextColor,
    hoverBackgroundColor,
    mobileColor,
    mobilePadding,
    mobileFontSize,
  } = element.attrs || {};

  let fontFamily: CSSProperties['fontFamily'] | undefined;
  let fontSize: CSSProperties['fontSize'] | undefined;
  let fontWeight: CSSProperties['fontWeight'] | undefined;
  let letterSpacing: CSSProperties['letterSpacing'] | undefined;
  if (element.attrs?.type !== 'logo') {
    fontFamily = element.attrs?.fontFamily;
    fontSize = element.attrs?.fontSize;
    fontWeight = element.attrs?.fontWeight;
    letterSpacing = (element.attrs as TNavbarItemV2ButtonElement['attrs'])?.letterSpacing;
  }

  const [firstChild, restChildren]: [React.ReactNode, React.ReactNode] = useMemo(() => {
    if (!Array.isArray(children)) {
      return [children, null];
    }

    if (children.length === 1) {
      return [children[0], null];
    }

    if (Array.isArray(children)) {
      const filteredRestChildren = children.slice(1).filter(Boolean);
      if (filteredRestChildren.length === 0) {
        return [children[0], null];
      }

      return [children[0], filteredRestChildren];
    }

    return [null, null];
  }, [children]);

  const RenderedDropdownReactNode = useMemo(() => {
    if (!children) return null;

    return <DropdownContent open={isOpen || shouldForceOpen}>{restChildren || firstChild}</DropdownContent>;
  }, [restChildren, shouldForceOpen, children, isOpen, firstChild]);

  return (
    <div
      className={classNames(styles.navbarItemAccordionV2, styles.navbarItemDropdownContainerV2)}
      data-mobile-dropdown="true"
    >
      <div
        {...props}
        role="presentation"
        {...customAttributes}
        className={classNames(styles.navbarItemDropdownV2, className, customAttributes?.className)}
        onClick={() => setIsOpen(!isOpen)}
        style={
          {
            '--padding': padding ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.padding,
            '--border-radius': borderRadius ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderRadius,
            ...getBackgroundColorStyle(background ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.background),
            '--text-color': computeColor(color ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.color),
            '--font-family': computeFont(fontFamily) || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.fontFamily,
            '--letter-spacing': letterSpacing ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.letterSpacing,
            ...getFontSizeVariables(fontSize ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.fontSize, mobileFontSize),
            '--font-weight': fontWeight ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.fontWeight,

            '--hover-text-color': hoverTextColor || color || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.color,
            '--hover-background-color':
              hoverBackgroundColor || background || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.background,
            '--hover-border-radius': evaluatedHoverBorderRadius,
            '--hover-border-color': hoverBorderColor || borderColor || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderColor,
            '--hover-border-style': hoverBorderStyle || borderStyle || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderStyle,
            '--hover-border-width': hoverBorderWidth || borderWidth || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderWidth,
            '--mobile-text-color': computeColor(mobileColor || color || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.color),
            '--mobile-padding': mobilePadding || padding || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.padding,
          } as CSSProperties
        }
      >
        {labelComponent || firstChild || label}
        {showCaret && <CaretDown size={16} weight="bold" className="text-wb-secondary" />}
      </div>
      {RenderedDropdownReactNode}
    </div>
  );
}
